import React from 'react'
import { resolveImageUrl } from '../../services/api'

export interface AllergenInfo {
  id: string
  name: string
  icon: string | null
}

interface AllergenIconsProps {
  /** Array of allergen IDs (e.g. from menu item or combined item + variant + addons) */
  allergenIds: string[]
  /** Map of id -> { id, name, icon } for display; names shown on hover */
  allergenMap: Record<string, AllergenInfo>
  /** Optional class for the container */
  className?: string
  /** Icon size in pixels (default 20) */
  size?: number
}

/**
 * Renders allergen icons only; allergen name is shown on hover (title attribute).
 * Used in POS, Waiter Module, and customer frontend.
 */
const AllergenIcons: React.FC<AllergenIconsProps> = ({
  allergenIds,
  allergenMap,
  className = '',
  size = 20
}) => {
  if (!allergenIds?.length) return null
  return (
    <div className={`flex flex-wrap items-center gap-1 ${className}`} role="list" aria-label="Allergens">
      {allergenIds.map((id) => {
        const info = allergenMap[id]
        if (!info) return null
        const name = info.name || 'Allergen'
        const iconUrl = info.icon ? resolveImageUrl(info.icon) : null
        return (
          <span
            key={id}
            role="listitem"
            className="inline-flex items-center justify-center rounded overflow-hidden bg-gray-100 dark:bg-gray-700 shrink-0"
            title={name}
            style={{ width: size, height: size }}
          >
            {iconUrl ? (
              <img
                src={iconUrl}
                alt=""
                className="w-full h-full object-contain"
                aria-hidden
              />
            ) : (
              <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400" aria-hidden>?</span>
            )}
          </span>
        )
      })}
    </div>
  )
}

export default AllergenIcons
